import { useEffect, useMemo, useState } from "react";
import { Plus, Search, MoreHorizontal, Users as UsersIcon, Trash2, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

function StatusBadge({ status }) {
  const isActive = status === "active";
  return (
    <span
      data-testid={`status-${status}`}
      className={
        "inline-flex h-5 items-center gap-1.5 rounded-full border px-2 text-[11px] font-medium " +
        (isActive
          ? "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-400"
          : "border-border bg-muted text-muted-foreground")
      }
    >
      <span
        className={
          "h-1.5 w-1.5 rounded-full " +
          (isActive ? "bg-emerald-500" : "bg-muted-foreground/60")
        }
      />
      {isActive ? "Aktív" : "Inaktív"}
    </span>
  );
}

function RoleBadge({ role }) {
  return (
    <span
      data-testid={`role-${role}`}
      className="inline-flex h-5 items-center rounded-md border border-border bg-muted px-2 font-mono text-[11px] text-muted-foreground"
    >
      {role}
    </span>
  );
}

function formatDateHu(value) {
  try {
    const d = new Date(value);
    return new Intl.DateTimeFormat("hu-HU", {
      year: "numeric",
      month: "short",
      day: "2-digit",
    }).format(d);
  } catch (e) {
    return "—";
  }
}

const emptyForm = {
  name: "",
  email: "",
  password: "",
  role: "user",
  status: "active",
};

export default function UsersPage() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/users");
      setUsers(data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Nem sikerült betölteni a felhasználókat.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return users;
    return users.filter(
      (u) =>
        u.name?.toLowerCase().includes(q) ||
        u.email?.toLowerCase().includes(q) ||
        u.role?.toLowerCase().includes(q)
    );
  }, [users, query]);

  const stats = useMemo(() => {
    const total = users.length;
    const active = users.filter((u) => u.status === "active").length;
    const admins = users.filter((u) => u.role === "admin").length;
    return { total, active, admins };
  }, [users]);

  const openCreate = () => {
    setForm(emptyForm);
    setFormError("");
    setDialogOpen(true);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setFormError("");
    try {
      const { data: saved } = await api.post("/users", form);
      toast.success("Felhasználó létrehozva.");
      setDialogOpen(false);
      setForm(emptyForm);
      if (saved) {
        setUsers((prev) => {
          const idx = prev.findIndex((u) => u._id === saved._id);
          if (idx >= 0) {
            const next = [...prev];
            next[idx] = saved;
            return next;
          }
          return [saved, ...prev];
        });
      }
      fetchUsers();
    } catch (err) {
      setFormError(formatApiError(err?.response?.data?.detail) || "Hiba történt.");
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (u) => {
    if (!window.confirm(`Biztosan törlöd "${u.name}" felhasználót?`)) return;
    try {
      await api.delete(`/users/${u._id}`);
      toast.success("Felhasználó törölve.");
      fetchUsers();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Nem sikerült törölni.");
    }
  };

  return (
    <div data-testid="users-page" className="space-y-8">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
            Csapat
          </p>
          <h1 className="mt-1 text-2xl font-semibold tracking-tight sm:text-3xl">
            Felhasználók
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            A cég munkatársainak és hozzáféréseinek kezelése.
          </p>
        </div>
        <Button
          data-testid="new-user-button"
          onClick={openCreate}
          className="h-9 self-start sm:self-auto"
        >
          <Plus className="h-4 w-4" strokeWidth={2} />
          Új felhasználó
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <StatCard label="Összes felhasználó" value={stats.total} testId="stat-total" />
        <StatCard label="Aktív" value={stats.active} testId="stat-active" />
        <StatCard label="Adminisztrátor" value={stats.admins} testId="stat-admins" />
      </div>

      {/* Search + Table card */}
      <div className="overflow-hidden rounded-xl border border-border bg-[hsl(var(--sipx-elevated))]">
        <div className="flex items-center gap-2 border-b border-border px-4 py-3">
          <Search
            className="h-4 w-4 text-muted-foreground"
            strokeWidth={1.75}
            aria-hidden
          />
          <input
            data-testid="users-search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Keresés név, e-mail vagy szerepkör alapján…"
            className="h-8 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
          />
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table
            data-testid="users-table"
            className="w-full min-w-[640px] text-sm"
          >
            <thead>
              <tr className="border-b border-border bg-muted/30 text-muted-foreground">
                <Th>Név</Th>
                <Th>E-mail</Th>
                <Th>Szerepkör</Th>
                <Th>Állapot</Th>
                <Th>Létrehozva</Th>
                <Th className="text-right pr-4">Műveletek</Th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <SkeletonRows />
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={6}>
                    <EmptyState onAction={openCreate} hasQuery={query.length > 0} />
                  </td>
                </tr>
              ) : (
                filtered.map((u) => (
                  <tr
                    key={u._id}
                    data-testid={`user-row-${u._id}`}
                    className="border-b border-border last:border-0 transition-colors hover:bg-foreground/[0.025]"
                  >
                    <Td>
                      <div className="flex items-center gap-3">
                        <span className="grid h-7 w-7 place-items-center rounded-full bg-foreground/90 text-background text-[10px] font-semibold">
                          {(u.name || u.email).slice(0, 2).toUpperCase()}
                        </span>
                        <span className="font-medium">{u.name}</span>
                      </div>
                    </Td>
                    <Td>
                      <span className="font-mono text-[12.5px] text-muted-foreground">
                        {u.email}
                      </span>
                    </Td>
                    <Td>
                      <RoleBadge role={u.role} />
                    </Td>
                    <Td>
                      <StatusBadge status={u.status} />
                    </Td>
                    <Td className="text-muted-foreground">
                      {formatDateHu(u.created_at)}
                    </Td>
                    <Td className="text-right pr-2">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            data-testid={`row-actions-${u._id}`}
                            aria-label="Sor műveletek"
                            className="grid h-8 w-8 place-items-center rounded-md text-muted-foreground hover:bg-foreground/[0.06] hover:text-foreground"
                          >
                            <MoreHorizontal className="h-4 w-4" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-40">
                          <DropdownMenuLabel>Műveletek</DropdownMenuLabel>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            data-testid={`delete-user-${u._id}`}
                            disabled={u._id === currentUser?._id}
                            onClick={() => onDelete(u)}
                            className="text-destructive focus:text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                            Törlés
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </Td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create User Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent
          data-testid="new-user-dialog"
          className="sm:max-w-[440px]"
        >
          <DialogHeader>
            <DialogTitle className="tracking-tight">Új felhasználó</DialogTitle>
            <DialogDescription>
              Hozz létre új munkatársi fiókot a rendszerben.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={onSubmit} className="space-y-4">
            <div className="grid grid-cols-1 gap-3">
              <Field label="Név">
                <Input
                  data-testid="form-name"
                  required
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="Kovács Péter"
                />
              </Field>
              <Field label="E-mail cím">
                <Input
                  data-testid="form-email"
                  required
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                  placeholder="nev@sipxgroup.hu"
                />
              </Field>
              <Field label="Jelszó">
                <Input
                  data-testid="form-password"
                  required
                  type="password"
                  minLength={6}
                  value={form.password}
                  onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                  placeholder="Legalább 6 karakter"
                />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Szerepkör">
                  <Select
                    value={form.role}
                    onValueChange={(v) => setForm((f) => ({ ...f, role: v }))}
                  >
                    <SelectTrigger data-testid="form-role">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">admin</SelectItem>
                      <SelectItem value="manager">manager</SelectItem>
                      <SelectItem value="user">user</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Állapot">
                  <Select
                    value={form.status}
                    onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}
                  >
                    <SelectTrigger data-testid="form-status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Aktív</SelectItem>
                      <SelectItem value="inactive">Inaktív</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
              </div>
            </div>

            {formError && (
              <div
                data-testid="form-error"
                className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive"
              >
                {formError}
              </div>
            )}

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setDialogOpen(false)}
                data-testid="cancel-new-user"
              >
                Mégse
              </Button>
              <Button type="submit" disabled={saving} data-testid="submit-new-user">
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Mentés…
                  </>
                ) : (
                  "Létrehozás"
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Th({ children, className = "" }) {
  return (
    <th
      className={
        "h-10 whitespace-nowrap px-4 text-left align-middle text-[11px] font-medium uppercase tracking-[0.12em] " +
        className
      }
    >
      {children}
    </th>
  );
}

function Td({ children, className = "" }) {
  return (
    <td className={"h-14 whitespace-nowrap px-4 align-middle " + className}>{children}</td>
  );
}

function Field({ label, children }) {
  return (
    <label className="block space-y-1.5">
      <span className="block text-xs font-medium text-muted-foreground">
        {label}
      </span>
      {children}
    </label>
  );
}

function StatCard({ label, value, testId }) {
  return (
    <div
      data-testid={testId}
      className="rounded-xl border border-border bg-[hsl(var(--sipx-elevated))] p-5"
    >
      <p className="text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
        {label}
      </p>
      <p className="mt-2 font-mono text-2xl font-semibold tracking-tight">
        {value}
      </p>
    </div>
  );
}

function SkeletonRows() {
  return (
    <>
      {[0, 1, 2, 3].map((i) => (
        <tr key={i} className="border-b border-border last:border-0">
          {[0, 1, 2, 3, 4, 5].map((j) => (
            <td key={j} className="h-14 px-4">
              <div className="h-3 w-3/4 animate-pulse rounded bg-muted" />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}

function EmptyState({ onAction, hasQuery }) {
  return (
    <div
      data-testid="users-empty-state"
      className="flex flex-col items-center justify-center gap-3 px-6 py-16 text-center"
    >
      <div className="grid h-10 w-10 place-items-center rounded-full border border-border bg-background">
        <UsersIcon className="h-4 w-4 text-muted-foreground" strokeWidth={1.75} />
      </div>
      <div className="space-y-1">
        <p className="text-sm font-medium">
          {hasQuery ? "Nincs találat" : "Nincsenek felhasználók"}
        </p>
        <p className="text-xs text-muted-foreground">
          {hasQuery
            ? "Próbálj meg másik keresést."
            : "Még nem adtál hozzá felhasználót a rendszerhez."}
        </p>
      </div>
      {!hasQuery && (
        <Button onClick={onAction} variant="outline" className="mt-2 h-8">
          <Plus className="h-3.5 w-3.5" />
          Új felhasználó hozzáadása
        </Button>
      )}
    </div>
  );
}
